import { useEffect, useState } from 'react';

import { CHAIN_PRESETS } from '@ai-studio/shared';

import {
  chainLabel,
  generatorOptionLabel,
  meshGenerators,
  modelRequestBody,
  pipelineOf,
  requestProblem,
  sameStages,
  type Pipeline,
} from '../../lib/generation.js';
import { acquirePolling, useGeneration } from '../../state/generation.js';
import { logEditor } from '../../state/console.js';
import { PipelineEditor } from '../generation/PipelineEditor.js';
import { SelectField } from '../ui/controls.js';
import { StageTag } from '../ui/GeneratorTag.js';
import { Icon } from '../ui/icons.js';
import { slugName } from './model-helpers.js';
import '../generation/generation.css';

type Mode = 'single' | 'chain';

const MODE_KEY = 'prism.model.genMode';
const DEFAULT_CHAIN = CHAIN_PRESETS[0]!;

function readMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === 'single' ? 'single' : 'chain';
  } catch {
    return 'chain';
  }
}

/** Where a model request writes: the open model, else a new models/<slug>.model.json. */
export function modelOutPath(openPath: string | null, prompt: string): string {
  return openPath ?? `models/${slugName(prompt.split(/\s+/).slice(0, 5).join(' '))}.model.json`;
}

/**
 * Local 3D generation from the Model tab: one generator, or a chain
 * (concept image -> shape -> polish) where each stage's output is rendered and
 * fed into the next. Which kinds are allowed lives in the Assistant window.
 */
export function ModelGenPanel({ prompt, openPath, projectId }: { prompt: string; openPath: string | null; projectId: number | null }) {
  const allowed = useGeneration((s) => s.settings.types.model.allow);
  const submit = useGeneration((s) => s.submit);
  const [mode, setModeState] = useState<Mode>(readMode);
  const [single, setSingle] = useState('trellis2');
  const [chain, setChain] = useState<Pipeline>(() => pipelineOf(DEFAULT_CHAIN.stages));
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const generators = meshGenerators();

  useEffect(() => {
    void useGeneration.getState().setProject(projectId);
    return projectId === null ? undefined : acquirePolling();
  }, [projectId]);

  const setMode = (m: Mode) => {
    setModeState(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      // The choice lasts for this session.
    }
  };

  const stages = mode === 'single' ? [single] : [...chain.stages];
  const out = modelOutPath(openPath, prompt);
  const body = modelRequestBody({ out, prompt, stages });
  const problem = requestProblem(body);
  const preset = CHAIN_PRESETS.find((p) => sameStages(p.stages, chain.stages));
  const blocked = !allowed ? 'Local 3D generation is off. Turn on "Allow local" for 3D models in Prisma\'s panel (Local generation button).' : null;

  async function generate() {
    if (busy || problem || blocked || projectId === null) return;
    setBusy(true);
    setSent(null);
    const id = await submit(body);
    setBusy(false);
    if (id) {
      setSent(`Requested ${chainLabel(stages)} -> ${out}. Follow it in Prisma's panel under Generations.`);
      logEditor('info', `Local generation requested: ${chainLabel(stages)} -> ${out}`);
    } else {
      setSent(useGeneration.getState().error ?? 'Could not start the generation.');
    }
  }

  return (
    <div className="gen-model-panel gen-form" aria-label="Local 3D generation">
      <div className="gen-row">
        <Icon name="local" size={14} className="ai-glyph" />
        <strong>Generate locally</strong>
        <span className="bar-spacer" />
        <div className="tb-group segmented gen-mode-tabs" role="tablist" aria-label="Generation mode">
          <button type="button" role="tab" aria-selected={mode === 'single'} className={`tb-btn${mode === 'single' ? ' is-active' : ''}`} onClick={() => setMode('single')}>
            Generator
          </button>
          <button type="button" role="tab" aria-selected={mode === 'chain'} className={`tb-btn${mode === 'chain' ? ' is-active' : ''}`} onClick={() => setMode('chain')}>
            Chain
          </button>
        </div>
      </div>

      {mode === 'single' ? (
        <div className="gen-form">
          <SelectField
            value={single}
            options={generators.map((g) => ({ value: g.id, label: generatorOptionLabel(g) }))}
            onChange={setSingle}
          />
          <StageTag id={single} time />
          <p className="gen-note">{generators.find((g) => g.id === single)?.specialty}</p>
        </div>
      ) : (
        <div className="gen-form">
          <div className="gen-preset-row">
            <SelectField
              value={preset?.id ?? 'custom'}
              options={[
                ...CHAIN_PRESETS.map((p) => ({ value: p.id, label: `${p.label} (${chainLabel(p.stages)})` })),
                { value: 'custom', label: 'Custom chain' },
              ]}
              onChange={(id) => {
                const p = CHAIN_PRESETS.find((x) => x.id === id);
                if (p) setChain(pipelineOf(p.stages));
              }}
            />
          </div>
          {preset && <p className="gen-note">{preset.note}</p>}
          <PipelineEditor mode="model" pipeline={chain} onChange={setChain} />
          <p className="gen-note">Each stage's output is rendered and fed into the next as an enhancement chain.</p>
        </div>
      )}

      <div className="gen-row">
        <button type="button" className="primary small" disabled={busy || !!problem || !!blocked || projectId === null} onClick={() => void generate()} title={blocked ?? problem ?? `Queue ${chainLabel(stages)}`}>
          <Icon name="sparkles" size={13} /> Generate locally
        </button>
        <code className="gen-out" title="Where the result is written">
          {out}
        </code>
      </div>
      {blocked && <p className="gen-error">{blocked}</p>}
      {!blocked && problem && prompt.trim() !== '' && <p className="gen-error">{problem}</p>}
      {!blocked && !prompt.trim() && <p className="gen-note">Describe the model in the box below first.</p>}
      {sent && <p className="gen-note">{sent}</p>}
    </div>
  );
}
